import { useAuth } from '@/hooks/useAuth';
import {
  Loader2, PlusCircle, Trash2, Edit, Lock, Unlock, Upload, Link2, Star, FileArchive,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { getAllModUpdates } from '@/lib/actions';
import { ModUpdate } from '@/lib/types';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from '@/firebase/config';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card } from '@/components/ui/card';

const saveFn = httpsCallable(functions, 'modRelease_save');
const setLockFn = httpsCallable(functions, 'modRelease_setLock');
const deleteFn = httpsCallable(functions, 'modRelease_delete');

function ModUpdateForm({
  onFormSuccess,
  updateToEdit,
}: {
  onFormSuccess: () => void;
  updateToEdit?: ModUpdate | null;
}) {
  const isEditing = !!updateToEdit;
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [title, setTitle] = useState(updateToEdit?.title || '');
  const [version, setVersion] = useState(updateToEdit?.version || '');
  const [description, setDescription] = useState(updateToEdit?.description || '');
  const [accentColor, setAccentColor] = useState(updateToEdit?.accentColor || '#10b981');
  const [badgeText, setBadgeText] = useState(updateToEdit?.badgeText || '');
  const [featured, setFeatured] = useState(updateToEdit?.featured ?? false);
  const [source, setSource] = useState<'file' | 'external'>(
    updateToEdit?.downloadType === 'external' ? 'external' : 'file'
  );
  const [externalUrl, setExternalUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!title.trim()) {
      toast({ title: 'Falta el título', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const id = updateToEdit?.id || crypto.randomUUID();
      let storagePath: string | undefined;
      let fileName: string | undefined;

      if (source === 'file' && file) {
        fileName = file.name;
        storagePath = `mod_releases/${id}/${file.name}`;
        // El jar de una versión no cambia nunca: se cachea a lo bestia (ver CACHE_SUBIDAS).
        await uploadBytes(ref(storage, storagePath), file, {
          cacheControl: 'public, max-age=31536000, immutable',
        });
      }

      await saveFn({
        id,
        title: title.trim(),
        version: version.trim(),
        description,
        accentColor,
        badgeText: badgeText.trim(),
        featured,
        ...(source === 'file' && storagePath ? { storagePath, fileName } : {}),
        ...(source === 'external' && externalUrl.trim() ? { externalUrl: externalUrl.trim() } : {}),
      });

      toast({
        title: isEditing ? 'Versión actualizada' : 'Versión publicada',
        description: 'La descarga queda BLOQUEADA hasta que la habilites en la lista.',
      });
      formRef.current?.reset();
      onFormSuccess();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo guardar.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="title">Título</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mod Cofrade 3.0" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="version">Versión</Label>
          <Input id="version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="3.0" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <RichTextarea id="description" name="description" rows={5}
          value={description} onChange={(e: any) => setDescription(e.target.value)} />
      </div>

      {/* Origen de la descarga */}
      <div className="space-y-2 rounded-lg border border-white/8 p-3">
        <Label>Archivo de descarga</Label>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={source === 'file' ? 'default' : 'outline'}
            className="gap-1.5" onClick={() => setSource('file')}>
            <Upload className="h-4 w-4" /> Subir archivo
          </Button>
          <Button type="button" size="sm" variant={source === 'external' ? 'default' : 'outline'}
            className="gap-1.5" onClick={() => setSource('external')}>
            <Link2 className="h-4 w-4" /> Enlace externo
          </Button>
        </div>
        {source === 'file' ? (
          <div className="space-y-1">
            <Input type="file" accept=".jar,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {isEditing && updateToEdit?.hasDownload && (
              <p className="text-xs text-muted-foreground">
                Ya hay un archivo subido. Sube uno nuevo solo si quieres reemplazarlo.
              </p>
            )}
          </div>
        ) : (
          <Input type="url" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://… (MediaFire, Mega, etc.)" />
        )}
      </div>

      {/* Personalización de la tarjeta */}
      <div className="grid grid-cols-2 gap-3 rounded-lg border border-white/8 p-3">
        <div className="space-y-2">
          <Label htmlFor="accentColor">Color de la tarjeta</Label>
          <div className="flex items-center gap-2">
            <input id="accentColor" type="color" value={accentColor}
              onChange={(e) => setAccentColor(e.target.value)}
              className="h-9 w-12 cursor-pointer rounded border border-white/10 bg-transparent" />
            <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="font-mono" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="badgeText">Etiqueta (badge)</Label>
          <Input id="badgeText" value={badgeText} onChange={(e) => setBadgeText(e.target.value)}
            placeholder="¡NUEVO! / BETA / HOTFIX" maxLength={30} />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <Switch id="featured" checked={featured} onCheckedChange={setFeatured} />
          <Label htmlFor="featured" className="flex items-center gap-1.5">
            <Star className="h-4 w-4 text-amber-400" /> Destacar esta versión (efecto resaltado)
          </Label>
        </div>
      </div>

      <DialogFooter>
        <DialogClose asChild><Button variant="outline" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditing ? 'Guardar cambios' : 'Publicar versión'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminModPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [updateToEdit, setUpdateToEdit] = useState<ModUpdate | null>(null);
  const { toast } = useToast();
  const [updates, setUpdates] = useState<ModUpdate[]>([]);
  const [isLoadingUpdates, setIsLoadingUpdates] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchUpdates = async () => {
    setIsLoadingUpdates(true);
    const { updates } = await getAllModUpdates();
    setUpdates(updates);
    setIsLoadingUpdates(false);
  };

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet');
    } else if (userData?.role === 'admin') {
      fetchUpdates();
    }
  }, [user, userData, loading, navigate]);

  const handleFormSuccess = () => {
    setIsDialogOpen(false);
    setUpdateToEdit(null);
    fetchUpdates();
  };

  const handleEditClick = (update: ModUpdate) => {
    setUpdateToEdit(update);
    setIsDialogOpen(true);
  };

  const handleNewClick = () => {
    setUpdateToEdit(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteFn({ id });
      toast({ title: 'Versión eliminada' });
      fetchUpdates();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo eliminar.', variant: 'destructive' });
    }
  };

  const toggleLock = async (update: ModUpdate) => {
    setTogglingId(update.id);
    try {
      const newLocked = !(update.locked ?? true);
      await setLockFn({ id: update.id, locked: newLocked });
      toast({
        title: newLocked ? '🔒 Descarga bloqueada' : '🔓 Descarga habilitada',
        description: newLocked
          ? 'Nadie puede descargar esta versión hasta que la vuelvas a habilitar.'
          : 'La descarga ya está disponible para los usuarios.',
      });
      fetchUpdates();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo cambiar.', variant: 'destructive' });
    } finally {
      setTogglingId(null);
    }
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="max-w-3xl">
          <h1 className="font-headline text-5xl font-bold text-primary">Gestionar Mod Cofrade</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Publica versiones, personaliza su tarjeta y controla el bloqueo de descarga.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" onClick={handleNewClick}><PlusCircle className="mr-2 h-5 w-5" /> Nueva versión</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{updateToEdit ? 'Editar versión' : 'Nueva versión del mod'}</DialogTitle>
              <DialogDescription>
                {updateToEdit
                  ? 'Modifica los campos necesarios.'
                  : 'Rellena los campos. La descarga se publica BLOQUEADA; la habilitas cuando quieras.'}
              </DialogDescription>
            </DialogHeader>
            <ModUpdateForm onFormSuccess={handleFormSuccess} updateToEdit={updateToEdit} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold font-headline">Versiones publicadas</h2>
        {isLoadingUpdates ? (
          <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : updates && updates.length > 0 ? (
          <div className="space-y-4">
            {updates.map((update) => {
              const locked = update.locked ?? true;
              return (
                <Card key={update.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between"
                  style={{ borderLeft: `4px solid ${update.accentColor || '#10b981'}` }}>
                  <div className="flex-grow">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-lg">{update.title}</h3>
                      {update.version && (
                        <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">v{update.version}</span>
                      )}
                      {update.badgeText && (
                        <span className="rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-white"
                          style={{ backgroundColor: update.accentColor || '#10b981' }}>
                          {update.badgeText}
                        </span>
                      )}
                      {update.featured && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{update.description}</p>
                    <p className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{format(new Date(update.date), 'dd/MM/yyyy', { locale: es })}</span>
                      {update.hasDownload
                        ? <span className="flex items-center gap-1"><FileArchive className="h-3 w-3" /> con descarga</span>
                        : <span className="opacity-60">sin archivo</span>}
                    </p>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-2">
                    {/* Toggle de bloqueo */}
                    <Button
                      variant={locked ? 'destructive' : 'default'}
                      size="sm"
                      disabled={togglingId === update.id}
                      onClick={() => toggleLock(update)}
                      className="gap-1.5"
                      style={!locked ? { backgroundColor: '#10b981' } : undefined}
                    >
                      {togglingId === update.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      {locked ? 'Bloqueada' : 'Habilitada'}
                    </Button>

                    <Button variant="ghost" size="icon" onClick={() => handleEditClick(update)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar esta versión?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Se borrará la versión y su archivo. Esta acción es permanente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(update.id)}
                            className="bg-destructive hover:bg-destructive/80">
                            Confirmar eliminación
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed py-16 text-center">
            <h3 className="mt-4 text-lg font-semibold">No hay versiones</h3>
            <p className="mt-1 text-sm text-muted-foreground">Crea la primera versión para empezar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
